import { useCallback, useState } from 'react';
import { useEvent } from '@soap/modules';

const MAX_ENTRIES = 200;

export type AiFoundryDebugLogDirection = 'prompt' | 'response';

export type AiFoundryDebugLogEntry = {
  id: string;
  sequenceId: string;
  direction: AiFoundryDebugLogDirection;
  endpointSuffix: string;
  text: string;
  timestamp: Date;
};

function readEventString(event: Record<string, unknown>, camel: string, pascal: string): string | undefined {
  const value = event[camel] ?? event[pascal];
  return value != null ? String(value) : undefined;
}

function parseDirection(raw: string | undefined): AiFoundryDebugLogDirection | null {
  if (raw === 'prompt' || raw === 'response') return raw;
  return null;
}

function parseTimestamp(raw: string | undefined): Date {
  if (!raw) return new Date();
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

function formatTimestampForMarkdown(date: Date): string {
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function formatAiFoundryDebugLogAsMarkdown(entries: AiFoundryDebugLogEntry[]): string {
  if (entries.length === 0) return '';

  return entries
    .map((entry) => {
      const label = entry.direction === 'prompt' ? 'Prompt' : 'Response';
      const body = entry.text || '(empty)';
      const fence = body.includes('```') ? '````' : '```';
      return [
        `## ${label} — ${entry.endpointSuffix}`,
        `*${formatTimestampForMarkdown(entry.timestamp)}* · \`${entry.sequenceId}\``,
        '',
        fence,
        body,
        fence,
      ].join('\n');
    })
    .join('\n\n');
}

/**
 * Accumulates live E438v1_AiFoundryDebugLogEntry events for the developer debug panel.
 */
export function useAiFoundryDebugLog(enabled = true) {
  const [entries, setEntries] = useState<AiFoundryDebugLogEntry[]>([]);

  const clearEntries = useCallback(() => {
    setEntries([]);
  }, []);

  useEvent({
    eventName: 'E438v1_AiFoundryDebugLogEntry',
    enabled,
    onEventReceived: (event: Record<string, unknown> | null | undefined) => {
      if (!event) return;

      const sequenceId = readEventString(event, 'e438_SequenceId', 'E438_SequenceId');
      const directionRaw = readEventString(event, 'e438_Direction', 'E438_Direction');
      const endpointSuffix = readEventString(event, 'e438_EndpointSuffix', 'E438_EndpointSuffix');
      const text = readEventString(event, 'e438_Text', 'E438_Text') ?? '';
      const timestampRaw = readEventString(event, 'e438_Timestamp', 'E438_Timestamp');

      const direction = parseDirection(directionRaw);
      if (!sequenceId || !direction || !endpointSuffix) return;

      const entry: AiFoundryDebugLogEntry = {
        id: `${sequenceId}-${direction}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        sequenceId,
        direction,
        endpointSuffix,
        text,
        timestamp: parseTimestamp(timestampRaw),
      };

      setEntries((prev) => {
        const next = [...prev, entry];
        if (next.length <= MAX_ENTRIES) return next;
        return next.slice(next.length - MAX_ENTRIES);
      });
    },
  });

  return { entries, clearEntries };
}
